import React, { useEffect, useState, useRef } from 'react';
interface DecryptedTextProps {
  text: string;
  speed?: number;
  maxIterations?: number;
  characters?: string;
  className?: string;
  parentClassName?: string;
  encryptedClassName?: string;
  /** ms before the animation kicks off */
  delay?: number;
}
/**
 * DecryptedText (reactbits.dev inspired)
 * Each character cycles through random scramble chars before settling on the
 * final character, with a staggered reveal across the string.
 * Perfect thematic match for a security/hacker brand.
 */
export function DecryptedText({
  text,
  speed = 50,
  maxIterations = 12,
  characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()_+',
  className = '',
  parentClassName = '',
  encryptedClassName = '',
  delay = 0
}: DecryptedTextProps) {
  const [displayed, setDisplayed] = useState<string>(text);
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;
    const timeoutId = setTimeout(() => {
      let iteration = 0;
      const interval = setInterval(() => {
        setRevealed(prev => {
          const next = new Set(prev);
          // Reveal one new character per few iterations, in order
          const targetRevealCount = Math.min(text.length, Math.floor(iteration / maxIterations * text.length) + 1);
          for (let i = 0; i < targetRevealCount; i++) next.add(i);
          return next;
        });
        setDisplayed(text.split('').map((ch, i) => {
          if (ch === ' ') return ' ';
          if (revealedRef.current.has(i)) return ch;
          return characters[Math.floor(Math.random() * characters.length)];
        }).join(''));
        iteration += 1;
        if (iteration > maxIterations + text.length) {
          clearInterval(interval);
          setDisplayed(text);
          setRevealed(new Set(text.split('').map((_, i) => i)));
        }
      }, speed);
    }, delay);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Keep a ref synced with the latest revealed set so the interval can read it
  const revealedRef = useRef(revealed);
  useEffect(() => {
    revealedRef.current = revealed;
  }, [revealed]);
  return <span className={parentClassName} aria-label={text} data-id="element-46">
      {displayed.split('').map((ch, i) => <span key={i} className={revealed.has(i) ? className : encryptedClassName || className} aria-hidden="true" data-id="element-47">
          {ch}
        </span>)}
    </span>;
}